import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';

interface QrCodeGeneratorProps {
  url: string;
  size?: number;
  className?: string;
}

const QrCodeGenerator: React.FC<QrCodeGeneratorProps> = ({
  url,
  size = 200,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canvasRef.current || !url) return;

    QRCode.toCanvas(canvasRef.current, url, {
      width: size,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: {
        dark: '#3E2723', // coffee-900 equivalent
        light: '#FFFFFF',
      },
    })
      .then(() => setError(null))
      .catch((err) => setError(err.message || 'Failed to generate QR code'));
  }, [url, size]);

  if (error) {
    return (
      <div className="flex items-center justify-center bg-red-50 text-red-600 rounded-lg p-4 text-sm border border-red-200">
        QR code error: {error}
      </div>
    );
  }

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <canvas
        ref={canvasRef}
        aria-label={`QR code for ${url}`}
        role="img"
        style={{ width: size, height: size }}
      />
    </div>
  );
};

/**
 * Generate a QR code as a data URL (PNG) for embedding in PDFs.
 */
export async function generateQrDataUrl(
  url: string,
  size: number = 300
): Promise<string> {
  return QRCode.toDataURL(url, {
    width: size,
    margin: 2,
    errorCorrectionLevel: 'M',
    color: {
      dark: '#3E2723',
      light: '#FFFFFF',
    },
  });
}

export default QrCodeGenerator;
